import { PlusOutlined } from '@ant-design/icons';
import {
  CategoryNames,
  FishingReport,
  KitIntent,
  Product,
  SeasonGroup,
  Species,
  State,
  StateTopWaterbody,
  Technique,
} from '@omniafishing/core';
import { Dropdown, Select } from 'antd';
import _ from 'lodash';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { apiV1 } from '../../lib/api';
import { randomKitIntentId } from '../../lib/kit';
import { getPercentage } from '../../lib/numbers';
import { OmniaQueryParams } from '../../lib/query_string';
import { setShopifyImgWidth } from '../../lib/set_shopify_img_width';
import { OmniaUrls } from '../../lib/urls';
import { RoutePaths } from '../../routes';
import {
  kitCategoryNames,
  KitInline,
  singleItemCategories,
} from '../../routes/get_started/kit_inline/kit_inline';
import { BadgeNew } from '../badge_new/badge_new';
import Loading from '../loading/loading';
import { OmniaButton } from '../omnia_button/omnia_button';
import { OmniaLinkButton } from '../omnia_button/omnia_link_button';
import { OmniaLink } from '../omnia_link/omnia_link';
import SvgFishingReport from '../svg/fishing_report';
import SvgHotbait from '../svg/hotbait';
import SvgTechniques from '../svg/new_product_transparent';
import SvgTackleBox from '../svg/tackle_box';
import { TechniqueFullWithReportCount } from '../waterbody_detail/waterbody_detail_sections/waterbody_detail_top_products_top_techniques/waterbody_detail_top_techniques';
import styles from './state_stats.less';

interface WaterbodyDetailStatsProps {
  onTechniqueClick: (t: TechniqueFullWithReportCount) => void;
  selectedSeasonGroup: SeasonGroup;
  selectedSpecies: Species;
  state: State;
  kitPreviewProducts: Product[];
  topTechniquesWithCounts: TechniqueFullWithReportCount[];
  topWaterbodies: StateTopWaterbody[];
  fishingReports30DaysCount: number;
  fishingReportsCount: number;
}

export const StateStats = (props: WaterbodyDetailStatsProps) => {
  const {
    onTechniqueClick,
    selectedSeasonGroup,
    selectedSpecies,
    state,
    topTechniquesWithCounts,
    topWaterbodies,
    kitPreviewProducts,
    fishingReports30DaysCount,
    fishingReportsCount,
  } = props;
  const { isDesktop } = useResponsive();
  const { stringify } = useQueryString();

  const VALUE_ALL_LAKES_IN_STATE = 'ALL_LAKES_IN_STATE';
  const [selectedWaterbodyUrlSlug, setSelectedWaterbodyUrlSlug] =
    useState<string>(VALUE_ALL_LAKES_IN_STATE);
  const selectedWaterbody = topWaterbodies.find(
    (sw) => sw.waterbody.url_slug === selectedWaterbodyUrlSlug
  )?.waterbody;
  const [selectedWaterbodyFishingReports, setSelectedWaterbodyFishingReports] = useState<
    FishingReport[]
  >([]);
  const [isKitOpen, setIsKitOpen] = React.useState(false);
  const [fishingIntents, setFishingIntents] = React.useState<KitIntent[]>([]);
  const [fishingIntentLoading, setFishingIntentLoading] = React.useState(false);

  const selectedTechniquesNames = fishingIntents.map((fi) => fi.technique.name);
  const unselectedTechniques = topTechniquesWithCounts.filter(
    (t) => !selectedTechniquesNames.includes(t.name) && t.reportCount > 0
  );

  const createFishingIntent = async (technique: Technique) => {
    setFishingIntentLoading(true);

    if (selectedWaterbody) {
      // with a waterbody, the kit will fetch waterbody recommendations
      setFishingIntents((currentIntents) => {
        return [
          ...currentIntents,
          {
            species: selectedSpecies,
            season_group: selectedSeasonGroup,
            waterbody: selectedWaterbody,
            technique,
            state: null,
            kit_products: [],
            id: randomKitIntentId(),
          },
        ];
      });
      setFishingIntentLoading(false);
    } else {
      // without the waterbody, we need to get state top recommendations and pass them to the kit
      const res = await apiV1.stateBestSellersFetch(state.slug, {
        season_group: selectedSeasonGroup.name,
        species: selectedSpecies.name,
        style: technique.name,
      });

      const hotbaitsSorted = _.orderBy(
        res.data.data.hotbaits,
        [
          (sh) => sh.fishing_reports.length,
          (sh) => sh.product.state_fishing_report_counters[state.abbr],
        ],
        ['desc', 'desc']
      );

      const allProducts = [
        ...hotbaitsSorted.map((sh) => sh.product),
        ...res.data.data.best_sellers.map((bs) => bs.product),
        ...res.data.data.recommendations,
      ];
      const productsByCategory = _.groupBy(allProducts, (p) => p.category_name);
      const products: Product[] = [];
      Object.entries(productsByCategory).map(([key, values]) => {
        const catName = key as CategoryNames;
        if (kitCategoryNames.includes(catName)) {
          const maxLength = singleItemCategories.includes(catName) ? 1 : 2;
          products.push(...values.filter((p) => p.in_stock).slice(0, maxLength));
        }
      });
      const productsOrdered = _.orderBy(
        products,
        (p) => _.indexOf(kitCategoryNames, p.category_name),
        ['asc']
      );
      setFishingIntents((currentIntents) => {
        return [
          ...currentIntents,
          {
            species: selectedSpecies,
            season_group: selectedSeasonGroup,
            waterbody: null,
            technique,
            products: productsOrdered,
            state: null,
            kit_products: [],
            id: randomKitIntentId(),
          },
        ];
      });
      // when we don't have a waterbody, we need to append fishing reports so we can get all of them accross technqiues
      setSelectedWaterbodyFishingReports((currentReports) => {
        return [...currentReports, ..._.flatten(hotbaitsSorted.map((sh) => sh.fishing_reports))];
      });
      setFishingIntentLoading(false);
    }
  };

  useEffect(() => {
    setSelectedWaterbodyFishingReports([]);
    if (selectedWaterbody?.url_slug) {
      apiV1.waterbodyFishingReportsFetch(selectedWaterbody.url_slug).then((res) => {
        setSelectedWaterbodyFishingReports(res.data.data);
      });
    }
  }, [selectedWaterbody?.url_slug]);

  const pieData = topTechniquesWithCounts.map((technique) => {
    return {
      title: technique.display_name,
      value: technique.reportCount,
      color: technique.color,
    };
  });

  const pieSlice = pieData.slice(0, 10);
  const hasReports = fishingReportsCount > 0;
  const topTechniquesSum = topTechniquesWithCounts.reduce(
    (accumulator, data) => accumulator + data.reportCount,
    0
  );

  const lakeOptions = [
    {
      label: `All lakes in ${state.name}`,
      value: VALUE_ALL_LAKES_IN_STATE,
    },
  ].concat(
    topWaterbodies.slice(0, 10).map((sw) => {
      return {
        label: sw.waterbody.primary_name,
        value: sw.waterbody.url_slug,
      };
    })
  );

  return (
    <div>
      <div className={styles.statSectionContainer}>
        <div className={styles.stat__topTechniques}>
          <span className={styles.statHeading}>
            <SvgTechniques />
            Top {hasReports && 'Reported'} Techniques
          </span>
          <div className={styles.statsTopTechniques}>
            {isDesktop && (
              <div className={styles.pie}>
                <PieChart
                  data={pieSlice}
                  radius={PieChart.defaultProps.radius - 6}
                  lineWidth={60}
                  startAngle={270}
                  animate
                />
              </div>
            )}
            <div className={styles.statsTechniques}>
              <ol className={styles.statTechniqueList}>
                {topTechniquesWithCounts.slice(0, 5).map((t) => {
                  return (
                    <li
                      key={t.name}
                      className={styles.statTechniqueItem}
                      onClick={() => {
                        onTechniqueClick(t);
                      }}
                    >
                      <div className={styles.techniqueNameColor}>
                        <span style={{ background: t.color }} className={styles.colorSwatch}></span>
                        <u>{t.display_name} </u>
                      </div>
                      <span>
                        {hasReports
                          ? getPercentage(t.reportCount, topTechniquesSum)
                          : getPercentage(1, topTechniquesWithCounts.length)}
                        %
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>

        <div className={styles.stat__fishingReport}>
          <span className={styles.statHeading}>
            <SvgFishingReport />
            Fishing Reports
          </span>
          <div>
            <span className={styles.statFishingReportsCount}>{fishingReportsCount} </span>
            <span className={styles.statFishingReports30Day}>
              <strong>
                {fishingReports30DaysCount} {pluralize('Report', fishingReports30DaysCount)}
              </strong>{' '}
              in the last 30 days {fishingReports30DaysCount > 0 && <BadgeNew />}
            </span>
          </div>

          <div className={styles.statFooter}>
            <OmniaLinkButton
              kind="tertiary"
              size="md"
              to={
                RoutePaths.FISHING_REPORTS +
                `?${stringify({
                  [OmniaQueryParams.state]: state.abbr,
                  [OmniaQueryParams.species]: selectedSpecies.name,
                  [OmniaQueryParams.season_group]: selectedSeasonGroup.name,
                })}`
              }
            >
              View Fishing Reports
            </OmniaLinkButton>
          </div>
        </div>

        <div className={styles.stat__kit}>
          <span className={styles.statHeading}>
            <SvgHotbait width={16} height={16} />
            Build a Kit
          </span>
          <div className={styles.dynamicKitCardImgs}>
            {kitPreviewProducts.slice(0, 5).map((p) => (
              <div className={styles.dynamicKitCardImg} key={p.sku}>
                <img src={setShopifyImgWidth(p.img_url, 100)} alt={p.title} />
              </div>
            ))}
          </div>
          <div className={styles.chooseLake}>
            <span className={styles.chooseHeading}>choose my lake</span>
            <Select
              className={styles.lakeSelect}
              options={lakeOptions}
              value={selectedWaterbodyUrlSlug}
              onChange={(val) => {
                setFishingIntents([]);
                setIsKitOpen(false);
                setSelectedWaterbodyUrlSlug(val);
              }}
            />
          </div>
          <div className={styles.statFooter}>
            <OmniaButton
              kind="primary"
              size="md"
              onPress={() => {
                if (fishingIntents.length === 0 && !fishingIntentLoading) {
                  createFishingIntent(topTechniquesWithCounts[0]);
                }
                setIsKitOpen((currentOpen) => !currentOpen);
              }}
            >
              <SvgTackleBox /> Build Your Kit
            </OmniaButton>
          </div>
        </div>
      </div>
      <div>
        <div className={styles.kit}>
          {fishingIntents.length > 0 && (
            <>
              {isKitOpen && (
                <>
                  <h3 className={styles.kitHeading}>Your Kit</h3>
                  <p className={styles.kitSubheading}>
                    For{' '}
                    {selectedWaterbody ? (
                      <OmniaLink
                        currentColor
                        underline
                        to={
                          OmniaUrls.waterbody(selectedWaterbody) +
                          `?${stringify({
                            [OmniaQueryParams.species]: selectedSpecies.name,
                            [OmniaQueryParams.season_group]: selectedSeasonGroup.name,
                          })}`
                        }
                        target="_blank"
                      >
                        {selectedWaterbody.primary_name}
                      </OmniaLink>
                    ) : (
                      state.name
                    )}{' '}
                    {selectedSeasonGroup.display_name} {selectedSpecies.display_name}
                  </p>
                </>
              )}
              <KitInline
                fishingIntents={fishingIntents}
                fishingReports={selectedWaterbodyFishingReports}
                showFooterLakeDetails={false}
                showFooterCloseButton={true}
                onFooterCloseClick={() => setIsKitOpen(false)}
                visible={isKitOpen}
                showTopWaterbody={selectedWaterbody == null}
              />
            </>
          )}
          {fishingIntentLoading && (
            <>
              <Loading text="Loading Kit Products" />
            </>
          )}
          {isKitOpen && fishingIntents.length > 0 && fishingIntents.length < 3 && (
            <>
              <OmniaButton kind="tertiary" size="md" className={styles.dropdownButton}>
                <Dropdown
                  menu={{
                    items: unselectedTechniques.map((t) => ({
                      label: `${t.display_name} ${getPercentage(t.reportCount, topTechniquesSum)}%`,
                      key: t.name,
                    })),
                    onClick: ({ key }) => {
                      createFishingIntent(topTechniquesWithCounts.find((t) => t.name === key));
                    },
                  }}
                  trigger={['click']}
                >
                  <span className={styles.dropdownButtonText}>
                    Add Technique <PlusOutlined />
                  </span>
                </Dropdown>
              </OmniaButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
